import {Component, EventEmitter, Input, Output, ViewChild, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivityFilter, DiscoverFilterBar, FilterSearch, InterestFilter} from '../../../../models/filter-search';
import {StringUtils} from '../../../utils/string.utils';
import {CommonConstant, discoverFilterSearchConst, OnboardingTourConstant} from '../../../app.constants';
import {Subject} from 'rxjs/Subject';
import {ActivityFiltersComponent} from '../../group-details/search-bar/activity-filters/activity-filters.component';
import {ActivitySearchCriteria, FilterRange} from '../../../../models/group-search';
import {CampaignService} from '../../../services/campaign/campaign.service';
import {MentionService} from '../../../services/mention/mention.service';
import {ObjectUtils} from '../../../utils/object.utils';
import {
  ActionDetails,
  ActionSource, ActivePages, EventTrackerService,
  Page, TargetType, UserActionType
} from '../../../services/event-tracker/event-tracker.service';
import {ActivePage} from '../../../../models/event-tracker';
import {DiscoverAudienceFilter, DiscoverInterestFilter} from '../../../../models/discover';
import {AudienceFilterComponent} from './audience-filter/audience-filter.component';
import {InterestFilterComponent} from './interest-filter/interest-filter.component';
import {UserService} from '../../../services/user.service';
import {TranslateService} from '@ngx-translate/core';
import {BlockedReason, InvalidAction, WallType} from '../../../../models/user';
import { ProjectSelectionComponent } from '../../measure/project-selection/project-selection.component';
import { AchooFilterBoxComponent } from '../../../core/achoo-filter-box/achoo-filter-box.component';
import { ReferenceDataService } from '../../../services/reference-data/reference-data.service';
import {AppSettings} from '../../../utils/app-settings';

@Component({
  selector: 'discover-search',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './discover-search.component.html',
  styleUrls: ['./discover-search.component.scss']
})
export class DiscoverSearchComponent implements OnInit {
  @ViewChild('activityFilterOnDiscover')
  activityFilter: ActivityFiltersComponent;
  @ViewChild('audienceFilterOnDiscover')
  audienceFilter: AudienceFilterComponent;
  @ViewChild('interestFilterOnDiscover')
  interestFilter: InterestFilterComponent;
  @ViewChild('projectSelection')
  projectSelection: ProjectSelectionComponent;
  @ViewChild('CountriesList')
  countriesList: AchooFilterBoxComponent;
  @ViewChild('GenderList') genderList: AchooFilterBoxComponent;
  @ViewChild('ActivityFilter') ActivityFilter;
  @ViewChild('InterestFilter') InterestFilter;
  @ViewChild('AudienceFilter') AudienceFilter;
  @ViewChild('PlatformList') PlatformList;

  @Input()
  public discoverFilterBar: DiscoverFilterBar;
  @Output()
  public searchEvent = new EventEmitter<any>();
  @Output()
  public invalidCriteriaEvent = new EventEmitter<any>();
  @Input()
  public filterSearch: FilterSearch;

  public currentInputMention: string | null;

  private searchChangeObserver: Subject<any>;
  private searchBounce: any | null;

  // used to show/hide activity filter
  public showActivityFilter: boolean;
  // used to know when use trigger activity filter event
  public hasFilterActivity = false;
  // used to know when use trigger interest filter event
  public hasFilterInterest = false;
  public showInterestFilter: boolean;
  // used to know when use trigger audience filter event
  public hasFilterAudience = false;
  public showAudienceFilter: boolean;

  // keep previous criteria which is using to compare the next criteria
  // only trigger search when criteria has been changed
  public previousFilterCriteria: FilterSearch;
  public selectedInterestFilter: DiscoverInterestFilter;
  public selectedAudienceFilter: DiscoverAudienceFilter;

  /**
   * Array of open filters, only search when this is empty
   */
  private openFilters: any | null;

  public countriesOfProject: number[] = [];
  public keywordsOfProject: string[] = [];
  public countriesAddManual: number[] = [];
  public keywordsAddManual: string[] = [];
  public toolTipText: string;

  constructor(private campaignService: CampaignService,
              private mentionService: MentionService,
              private userService: UserService,
              private translate: TranslateService,
              private eventTracker: EventTrackerService,
              private referenceDataService: ReferenceDataService) {
    this.openFilters = new Set();
  }

  ngOnInit() {
    this.exposeTourFunctions();
  }

  public get discoverFollowerEnabled(): boolean {
    return AppSettings.isEnableDiscoverFollower();
  }

  onFilterOpen(label, t) {
    if (t) {
      t.close();
    }
    // Clear existing timeout if set
    if (this.searchBounce) {
      clearTimeout(this.searchBounce);
    }

    // Add filter to tracker (used to detect when all filters are closed)
    this.openFilters.add(label);
  }

  onFilterClose(event, label) {
    // Mark filter as closed in tracker if specified
    if (label) {
      this.openFilters.delete(label);
    }

    // check whether the new filter criteria has changed with the previous
    // do nothing if the criteria is not changed
    if (!this.hasChangeFilterCriteria(this.filterSearch)) {
      return;
    }
    if (label === 'projects') {
      // free user not allow search by project
      if (this.userService.isFreeUser()) {
        return;
      } else {
        this.updateFilterByProject(this.filterSearch.projects);
      }
    }

    // validate search criteria first
    if (!this.validateSearchCriteria(event)) {
      // update previous filter criteria
      this.previousFilterCriteria = ObjectUtils.deepClone(this.filterSearch);
      this.invalidCriteriaEvent.emit();
      return;
    }

    // Clear existing timeout if set
    if (this.searchBounce) {
      clearTimeout(this.searchBounce);
    }

    // Set new timeout if all filters are closed
    if (this.openFilters.size === 0) {
      this.searchBounce = setTimeout(() => this.executeDiscoverSearch(), CommonConstant.DISCOVER_DEBOUNCE_TIME);
    }
  }

  executeDiscoverSearch() {
    this.filterSearch.limit = discoverFilterSearchConst.limit ;
    this.filterSearch.period = discoverFilterSearchConst.period;
    this.filterSearch.offset = discoverFilterSearchConst.offset;
    if (!this.filterSearch.orderBy) {
      this.filterSearch.orderBy = discoverFilterSearchConst.orderBy;
    }
    // update previous filter criteria
    this.previousFilterCriteria = ObjectUtils.deepClone(this.filterSearch);
    this.searchEvent.emit(this.filterSearch);
  }

  onDeleteMention(index: number) {
    this.filterSearch.mentions.splice(index, 1);

    // Start bounce for search
    this.onFilterClose(null, null);
  }

  mentionInputKeyDown(evt) {
    if (!(evt instanceof KeyboardEvent)) {
      return;
    }
    if (!evt || evt.keyCode !== 13) {
      return;
    }
    if (!this.currentInputMention || StringUtils.trim(this.currentInputMention) === '') {
      return;
    }
    // check the existing of new mention
    this.processMention(this.currentInputMention);
    // start searching by mention
    if (this.filterSearch.mentions.indexOf(this.currentInputMention) < 0) {
      this.filterSearch.mentions.push(this.currentInputMention);
    }
    this.keywordsAddManual.push(this.currentInputMention);
    this.currentInputMention = null;

    if ('activeElement' in document) {
      (document.activeElement as HTMLElement).blur();
    }
  }

  onFocusOutMentionInput() {
    this.onFilterClose(this.filterSearch, 'mention');
  }

  private processMention(mentionName) {
    const mention = this.campaignService.parseMentions(mentionName);
    // TODO: currently just use global language as temporaty
    mention.language.push(0);
    const activePage: ActivePage = {
      type: ActivePages.DISCOVER
    };
    this.mentionService.processNewMention(mention, undefined, undefined, activePage, ActionSource.PAGE, false);
  }

  private hasChangeFilterCriteria(criteria: FilterSearch) {
    if (!this.previousFilterCriteria) {
      return true;
    }
    // ignored some none-user setting values
    // offset, limit, period can not be changed in search bar
    const previousCriteria = Object.assign({}, this.previousFilterCriteria, {limit: 0, offset: 0, period: 0, searchAfter: []});
    const nextCriteria = Object.assign({}, criteria, {limit: 0, offset: 0, period: 0, searchAfter: []});
    return !ObjectUtils.isEquals(nextCriteria, previousCriteria);
  }

  onClickActivityFilters(e, t) {
    e.stopPropagation();
    this.closeInterestFilterWithoutTriggeringSearch();
    this.closeAudienceFilterWithoutTriggeringSearch();
    if (this.showActivityFilter) {
      this.showActivityFilter = false;
      this.onFilterClose(this.filterSearch, 'activity');
    } else {
      this.showActivityFilter = true;
      this.onFilterOpen('activity', t);
    }
  }

  searchDiscoverByActivity(criteria: ActivitySearchCriteria) {
    // update reach search criteria
    if (criteria.selectedReach) {
      this.hasFilterActivity = true;
      this.filterSearch.reachFrom =  criteria.selectedReach.from;
      this.filterSearch.reachTo =  criteria.selectedReach.to;
    }
    /**
     * @Deprecated
     * Replace discover search by Engagement by Follower
     * https://www.pivotaltracker.com/story/show/175815280
     */
    if (!AppSettings.isEnableDiscoverFollower() && criteria.selectedEngagement) {
      this.hasFilterActivity = true;
      this.filterSearch.engagementFrom =  criteria.selectedEngagement.from;
      this.filterSearch.engagementTo =  criteria.selectedEngagement.to;
    }
    if (AppSettings.isEnableDiscoverFollower() && criteria.selectedFollower) {
      this.hasFilterActivity = true;
      this.filterSearch.followerFrom =  criteria.selectedFollower.from;
      this.filterSearch.followerTo =  criteria.selectedFollower.to;
    }

    // update impact search criteria
    if (criteria.selectedImpact) {
      this.hasFilterActivity = true;
      this.filterSearch.impactFrom =  criteria.selectedImpact.from;
      this.filterSearch.impactTo =  criteria.selectedImpact.to;
    }

    this.showActivityFilter = false;
    this.onFilterClose(this.filterSearch, 'activity');
  }

  resetActivityFilters() {
    // reset reach condition
    this.filterSearch.reachFrom = undefined;
    this.filterSearch.reachTo = undefined;

    // reset engagement condition
    this.filterSearch.engagementFrom = undefined;
    this.filterSearch.engagementTo = undefined;

    // reset impact condition
    this.filterSearch.impactFrom = undefined;
    this.filterSearch.impactTo = undefined;

    // reset follower condition
    this.filterSearch.followerFrom = undefined;
    this.filterSearch.followerTo = undefined;

    // reset filter box
    this.hasFilterActivity = false;
  }

  onHideActivityFilters(e) {
    this.searchDiscoverByActivity(e);
  }

  /**
   * handle search by Interest filter
   * @param selectedCountries
   */
  onClickInterestFilters(e, t) {
    e.stopPropagation();
    this.closeActivityFilterWithoutTriggeringSearch();
    this.closeAudienceFilterWithoutTriggeringSearch();
    if (this.showInterestFilter) {
      this.showInterestFilter = false;
      this.onFilterClose(this.filterSearch, 'interest');
    } else {
      this.showInterestFilter = true;
      this.onFilterOpen('interest', t);
    }
  }
  searchDiscoverByInterestFilter(interestFilter: DiscoverInterestFilter) {
    this.selectedInterestFilter = interestFilter;
    this.filterSearch.interestFilters = [];
    this.hasFilterInterest = false;
    // update reach search criteria
    if (interestFilter.topicPercentage && interestFilter.topicPercentage.value.length > 0) {
      this.hasFilterInterest = true;
      this.filterSearch.interestFilters.push({
        name: 'topic_percentage',
        value: interestFilter.topicPercentage.value.join(','),
        from: interestFilter.topicPercentage.range ? interestFilter.topicPercentage.range.from / 100 : 0,
        to: interestFilter.topicPercentage.range ? interestFilter.topicPercentage.range.to / 100 : 1
      });
    }

    if (interestFilter.topIndustries && interestFilter.topIndustries.value.length > 0) {
      this.hasFilterInterest = true;
      this.filterSearch.interestFilters.push({
        name: 'top_industries',
        value: interestFilter.topIndustries.value.join(','),
        from: interestFilter.topIndustries.range ? interestFilter.topIndustries.range.from / 100 : 0,
        to: interestFilter.topIndustries.range ? interestFilter.topIndustries.range.to / 100 : 1
      });
    }

    this.showInterestFilter = false;
    this.onFilterClose(this.filterSearch, 'interest');
  }

  resetInterestFilters(clearSelectedValue?) {
    // reset interest filter values
    this.filterSearch.interestFilters = [];
    this.hasFilterInterest = false;
    if (clearSelectedValue) {
      this.selectedInterestFilter = null;
    }
  }

  onHideInterestFilters(e) {
    this.searchDiscoverByInterestFilter(e);
  }

  /**
   * Handle search by Audience Filter
   */
  onClickAudienceFilters(e, t) {
    e.stopPropagation();
    this.closeActivityFilterWithoutTriggeringSearch();
    this.closeInterestFilterWithoutTriggeringSearch();
    if (this.showAudienceFilter) {
      this.showAudienceFilter = false;
      this.onFilterClose(this.filterSearch, 'audience');
    } else {
      this.showAudienceFilter = true;
      this.onFilterOpen('audience', t);
    }
  }

  searchDiscoverByAudience(audienceFilter: DiscoverAudienceFilter) {
    this.selectedAudienceFilter = audienceFilter;
    this.filterSearch.audienceFilters = [];
    this.hasFilterAudience = false;
    // update reach search criteria
    if (audienceFilter.gender && audienceFilter.gender.value.length > 0) {
      this.hasFilterAudience = true;
      this.filterSearch.audienceFilters.push({
        name: 'gender',
        value: audienceFilter.gender.value.join(','),
        from: audienceFilter.gender.range ? audienceFilter.gender.range.from / 100 : 0,
        to: audienceFilter.gender.range ? audienceFilter.gender.range.to / 100 : 1
      });
    }

    // update engagement search criteria
    if (audienceFilter.age && audienceFilter.age.value.length > 0) {
      this.hasFilterAudience = true;
      this.filterSearch.audienceFilters.push({
        name: 'age',
        value: audienceFilter.age.value.join(','),
        from: audienceFilter.age.range ? audienceFilter.age.range.from / 100 : 0,
        to: audienceFilter.age.range ? audienceFilter.age.range.to / 100 : 1
      });
    }

    // update impact search criteria
    if (audienceFilter.country && audienceFilter.country.value.length > 0) {
      this.hasFilterAudience = true;
      this.filterSearch.audienceFilters.push({
        name: 'country_code',
        value: audienceFilter.country.value.join(','),
        from: audienceFilter.country.range ? audienceFilter.country.range.from / 100 : 0,
        to: audienceFilter.country.range ? audienceFilter.country.range.to / 100 : 1
      });
    }

    this.showAudienceFilter = false;
    this.onFilterClose(this.filterSearch, 'audience');
  }

  resetAudienceFilters(clearSelectedValue?) {
    // reset reach condition
    this.filterSearch.audienceFilters = [];

    // reset filter box
    this.hasFilterAudience = false;

    // reset selected filter criteria
    if (clearSelectedValue) {
      this.selectedAudienceFilter = null;
    }
  }

  onHideAudienceFilters(e) {
    this.searchDiscoverByAudience(e);
  }

  onSelectCountries(selectedCountries) {
    const selectedCountryIds = selectedCountries.map(c => c.id);
    this.countriesOfProject = this.countriesOfProject.filter((c) => selectedCountryIds.indexOf(c) >= 0);
    this.countriesAddManual = selectedCountryIds.filter(c => this.countriesOfProject.indexOf(c) < 0);
  }

  getActivityFilter(filterSearch: FilterSearch) {
    return new ActivityFilter(
      filterSearch.reachFrom, filterSearch.reachTo,
      filterSearch.engagementFrom, filterSearch.engagementTo,
      filterSearch.impactFrom, filterSearch.impactTo,
      filterSearch.followerFrom, filterSearch.followerTo);
  }

  private closeActivityFilterWithoutTriggeringSearch() {
    this.showActivityFilter = false;
    this.openFilters.delete('activity');
  }

  private closeInterestFilterWithoutTriggeringSearch() {
    this.showInterestFilter = false;
    this.openFilters.delete('interest');
  }

  private closeAudienceFilterWithoutTriggeringSearch() {
    this.showAudienceFilter = false;
    this.openFilters.delete('audience');
  }

  /**
   * must select at least country, platform or topic to trigger the search event
   * @param criteria
   * @returns {boolean}
   */
  private validateSearchCriteria(criteria) {
    const isValidBasicFilters = this.filterSearch.countries.length > 0
      || this.filterSearch.platforms.length > 0
      || this.filterSearch.interestFilters.length > 0
      || this.filterSearch.mentions.length > 0
      || this.filterSearch.audienceFilters.length > 0
      || this.filterSearch.genders.length > 0
      || this.filterSearch.projects.length > 0
      || this.filterSearch.reachTo
      || this.filterSearch.reachFrom
      || this.filterSearch.impactTo
      || this.filterSearch.impactFrom;
    if (AppSettings.isEnableDiscoverFollower()) {
      return isValidBasicFilters || this.filterSearch.followerTo || this.filterSearch.followerFrom;
    } else {
      return isValidBasicFilters || this.filterSearch.engagementTo || this.filterSearch.engagementFrom;
    }
  }

  private updateFilterByProject(selectedProjectids) {
    const selectProjects = this.discoverFilterBar.projects.filter((pro) => selectedProjectids.indexOf(pro.id) >= 0);
    const projectKeywords = new Set();
    const projectCountries = new Set();
    selectProjects.forEach((project) => {
      project.keywords.forEach((mention) => {
        projectKeywords.add(mention.keyword);
      });

      project.countries.forEach((country) => {
        projectCountries.add(country);
      });
    });

    const manualAddedCountries = this.filterSearch.countries.filter(c => this.countriesAddManual.indexOf(c) >= 0);
    this.countriesOfProject = Array.from(projectCountries.values());

    const manualAddedKeywords = this.filterSearch.mentions.filter(m => this.keywordsAddManual.indexOf(m) >= 0);
    this.keywordsOfProject = Array.from(projectKeywords.values());

    this.filterSearch.countries = Array.from(new Set([...manualAddedCountries, ...this.countriesOfProject]));
    this.filterSearch.mentions = Array.from(new Set([...manualAddedKeywords, ...this.keywordsOfProject]));
  }

  eventDataForSegment(name) {
    return {
      action: UserActionType.ACTION,
      item: Page.DISCOVER,
      target: {
        type: TargetType.FILTER,
        name
      },
      params: {
        action: UserActionType.TOGGLE
      }
    };
  }

  private exposeTourFunctions() {
    const simulatedMouseEvt = new MouseEvent('click');
    const activityFilterCriteria = new ActivitySearchCriteria();
    const interestFilterCriteria = new DiscoverInterestFilter();
    const audienceFilterCriteria = new DiscoverAudienceFilter();
    window['tourFunctions'] = {
      addInputToMentionBar: (inputString: string) => {
        this.currentInputMention = inputString;
      },
      submitMention: () => {
        this.onSubmitMention();
      },
      activateFilter: (inputParam: number) => {
        this.openOrCloseFilter(inputParam, true);
      },
      selectOption: (filterTypeInput: number, selectedOption: number) => {
        this.selectFilterOption(filterTypeInput, selectedOption);
      },
      submitFilter: (inputParam: number) => {
        this.openOrCloseFilter(inputParam , false);
      },
      activateActivityFilter: () => {
        this.onClickActivityFilters(simulatedMouseEvt, this.ActivityFilter.tooltipBox);
      },
      selectActivityOption: (childOption: number, minValue: number, maxValue: number) => {
        this.selectActivityFilter(childOption, minValue, maxValue, activityFilterCriteria);
      },
      submitActivityFilter: () => {
        this.searchDiscoverByActivity(activityFilterCriteria);
      },
      activateInterestsFilter: () => {
        this.onClickInterestFilters(simulatedMouseEvt, this.InterestFilter.tooltipBox);
      },
      selectInterestsOption: (childListIndex, optionIndex, minValue: number, maxValue: number) => {
        this.selectInterestFilter(childListIndex, optionIndex, minValue, maxValue, interestFilterCriteria);
      },
      submitInterestsFilter: () => {
        this.searchDiscoverByInterestFilter(interestFilterCriteria);
      },
      activateAudienceFilter: () => {
        this.onClickAudienceFilters(simulatedMouseEvt, this.AudienceFilter.tooltipBox);
      },
      selectAudienceOption: (childListIndex, optionIndex, minValue: number, maxValue: number) => {
        this.selectAudienceFilter(childListIndex, optionIndex, minValue, maxValue, audienceFilterCriteria);
      },
      submitAudienceFilter: () => {
        this.searchDiscoverByAudience(audienceFilterCriteria);
      }
    };
  }

  private onSubmitMention() {
    const mockEnterKeyEvent = new KeyboardEvent('keydown');
    Object.defineProperty(mockEnterKeyEvent, 'keyCode', {
        get : () => 13
    });
    this.mentionInputKeyDown(mockEnterKeyEvent);
    this.executeDiscoverSearch();
  }

  private openOrCloseFilter(inputParam, isActivated) {
    if (isNaN(inputParam)) {
      return;
    }
    const simulatedFocusEvt = new FocusEvent('blur');
    const filterType = OnboardingTourConstant.discoverFilterLabelList.find(item => item.id === inputParam);
    if (filterType.id === OnboardingTourConstant.filterIndex0) {
      this.triggerCountryDropdown(isActivated, filterType, simulatedFocusEvt);
    }
    if (filterType.id === OnboardingTourConstant.filterIndex1) {
      this.triggerGenderList(isActivated, filterType, simulatedFocusEvt);
    }
    if (filterType.id === OnboardingTourConstant.filterIndex2) {
      this.triggerPlatformList(isActivated, filterType, simulatedFocusEvt);
    }
    if (filterType.id === OnboardingTourConstant.filterIndex3) {
      this.triggerProjectList(isActivated, filterType, simulatedFocusEvt);
    }
  }

  private triggerCountryDropdown(isActivated, filterType, event) {
    if (!this.countriesList) {
      return;
    }
    if (isActivated) {
      this.countriesList.ngSelect.open();
    } else {
      this.countriesList.ngSelect.close();
      this.onFilterClose(event, filterType.name);
    }
  }

  private triggerGenderList(isActivated, filterType, event) {
    if (!this.genderList) {
      return;
    }
    if (isActivated) {
      this.genderList.ngSelect.open();
    } else {
      this.genderList.ngSelect.close();
      this.onFilterClose(event, filterType.name);
    }
  }

  private triggerPlatformList(isActivated, filterType, event) {
    if (!this.PlatformList) {
      return;
    }
    if (isActivated) {
      this.PlatformList.ngSelect.open();
    } else {
      this.PlatformList.ngSelect.close();
      this.onFilterClose(event, filterType.name);
    }
  }

  private triggerProjectList(isActivated, filterType, event) {
    if (!this.projectSelection) {
      return;
    }
    if (isActivated) {
      this.projectSelection.ngSelect.open();
    } else {
      this.projectSelection.ngSelect.close();
      this.onFilterClose(event, filterType.name);
    }
  }

  private selectFilterOption(filterTypeInput, selectedOption) {
    if (isNaN(filterTypeInput) || isNaN(selectedOption)) {
      return;
    }
    const filterType = OnboardingTourConstant.discoverFilterLabelList.find(item => item.id === filterTypeInput);
    if (filterType.id === OnboardingTourConstant.filterIndex0) {
      this.filterSearch.countries = [selectedOption];
      this.countriesList.model = this.filterSearch.countries;
    }
    if (filterType.id === OnboardingTourConstant.filterIndex1) {
      this.filterSearch.genders = [selectedOption];
      this.genderList.model = this.filterSearch.genders;
    }
    if (filterType.id === OnboardingTourConstant.filterIndex2) {
      this.filterSearch.platforms = [selectedOption];
      this.PlatformList.model = this.filterSearch.platforms;
    }
    if (filterType.id === OnboardingTourConstant.filterIndex3) {
      this.filterSearch.projects = [selectedOption];
      this.projectSelection.model = this.filterSearch.projects;
    }
  }

  private selectActivityFilter(childOption: number, minValue: number, maxValue: number, filterCriteria: ActivitySearchCriteria) {
    if (childOption === OnboardingTourConstant.filterIndex0) {
      filterCriteria.selectedReach = {from: minValue, to: maxValue};
      this.activityFilter.reachRangeSlider.updateRangeValue(minValue, maxValue);
    }
    if (!AppSettings.isEnableDiscoverFollower() && childOption === OnboardingTourConstant.filterIndex1) {
      filterCriteria.selectedEngagement = {from: minValue, to: maxValue};
      this.activityFilter.engagementRangeSlider.updateRangeValue(minValue, maxValue);
    }
    if (AppSettings.isEnableDiscoverFollower() && childOption === OnboardingTourConstant.filterIndex1) {
      filterCriteria.selectedFollower = {from: minValue, to: maxValue};
      this.activityFilter.followerRangeSlider.updateRangeValue(minValue, maxValue);
    }

    if (childOption === OnboardingTourConstant.filterIndex2) {
      filterCriteria.selectedImpact = this.formatInputValueToPercentage({from: minValue, to: maxValue});
      this.activityFilter.impactRangeSlider.updateRangeValue(filterCriteria.selectedImpact.from, filterCriteria.selectedImpact.to);
    }
  }

  private selectInterestFilter(childListIndex, optionIndex, minValue, maxValue, interestFilterCriteria) {
    const formattedRangeValue = this.formatInputValueToPercentage({from: minValue, to: maxValue});
    if (childListIndex === OnboardingTourConstant.filterIndex0) {
      interestFilterCriteria.topicPercentage.value = [optionIndex];
      interestFilterCriteria.topicPercentage.range = formattedRangeValue;
      this.interestFilter.topicFilterBox.model = interestFilterCriteria.topicPercentage.value;
      this.interestFilter.topicRangeSlider.updateRangeValue(formattedRangeValue.from, formattedRangeValue.to);
    }
    if (childListIndex === OnboardingTourConstant.filterIndex1) {
      interestFilterCriteria.topIndustries.value = [optionIndex];
      interestFilterCriteria.topIndustries.range = formattedRangeValue;
      this.interestFilter.industriesFilterBox.model = interestFilterCriteria.topIndustries.value;
      this.interestFilter.industrieseRangeSlider.updateRangeValue(formattedRangeValue.from, formattedRangeValue.to);
    }
  }

  private selectAudienceFilter(childListIndex, optionIndex, minValue, maxValue, audienceFilterCriteria) {
    const formattedRangeValue = this.formatInputValueToPercentage({from: minValue, to: maxValue});
    if (childListIndex === OnboardingTourConstant.filterIndex0) {
      this.selectGenderInAudienceFilter(optionIndex, formattedRangeValue, audienceFilterCriteria);
    }
    if (childListIndex === OnboardingTourConstant.filterIndex1) {
      this.selectAgeInAudienceFilter(optionIndex, formattedRangeValue, audienceFilterCriteria);
    }
    if (childListIndex === OnboardingTourConstant.filterIndex2) {
      this.selectCountryInAudienceFilter(optionIndex, formattedRangeValue, audienceFilterCriteria);
    }
  }

  private selectGenderInAudienceFilter(optionIndex, formattedRangeValue, audienceFilterCriteria) {
    if (optionIndex === OnboardingTourConstant.filterIndex0) {
      audienceFilterCriteria.gender.value = [OnboardingTourConstant.genderList.female];
    } else {
      audienceFilterCriteria.gender.value = [OnboardingTourConstant.genderList.male];
    }
    audienceFilterCriteria.gender.range = formattedRangeValue;
    this.audienceFilter.genderFilterBox.model = audienceFilterCriteria.gender.value;
    this.audienceFilter.genderRangeSlider.updateRangeValue(formattedRangeValue.from, formattedRangeValue.to);
  }

  private selectAgeInAudienceFilter(optionIndex, formattedRangeValue, audienceFilterCriteria) {
    const filteredAgeRange = optionIndex ? OnboardingTourConstant.ageList.find(item => item.id === optionIndex) : undefined;
    if (!filteredAgeRange) {
      return;
    }
    audienceFilterCriteria.age.value = [filteredAgeRange.range];
    audienceFilterCriteria.age.range = formattedRangeValue;
    this.audienceFilter.ageFilterBox.model = audienceFilterCriteria.age.value;
    this.audienceFilter.ageRangeSlider.updateRangeValue(formattedRangeValue.from, formattedRangeValue.to);
  }

  private selectCountryInAudienceFilter(optionIndex, formattedRangeValue, audienceFilterCriteria) {
    const filteredCountry = optionIndex ? this.referenceDataService.countries.find(item => item.id === optionIndex) : undefined;
    if (!filteredCountry) {
      return;
    }
    audienceFilterCriteria.country.value = [filteredCountry.code];
    audienceFilterCriteria.country.range = formattedRangeValue;
    this.audienceFilter.countryFilterBox.model = audienceFilterCriteria.country.value;
    this.audienceFilter.countryRangeSlider.updateRangeValue(formattedRangeValue.from, formattedRangeValue.to);
  }

  private formatInputValueToPercentage(value: FilterRange) {
    value.from = value.from && value.from >= OnboardingTourConstant.defaultMinValue ? value.from * OnboardingTourConstant.defaultMaxValue : OnboardingTourConstant.defaultMinValue;
    value.to = value.to && value.to <= OnboardingTourConstant.defaultMaxPercentValue ? value.to * OnboardingTourConstant.defaultMaxValue : OnboardingTourConstant.defaultMaxValue;
    return value;
  }
}



// WEBPACK FOOTER //
// ./src/app/main/discover/discover-search/discover-search.component.ts